import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Bottle, CreateBottleDto, UpdateBottleDto, ArchiveBottleDto } from '../../../domain/entities';
import { bottlesApi, BottlesFilter, PaginatedResponse } from '../../../data/api';

interface BottlesState {
  bottles: Bottle[];
  archivedBottles: Bottle[];
  currentBottle: Bottle | null;
  totalBottles: number;
  totalArchivedBottles: number;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
}

const initialState: BottlesState = {
  bottles: [],
  archivedBottles: [],
  currentBottle: null,
  totalBottles: 0,
  totalArchivedBottles: 0,
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
};

export const fetchBottles = createAsyncThunk(
  'bottles/fetchBottles',
  async (filter?: BottlesFilter) => {
    console.log('🍷 Redux - fetchBottles appelé avec filtres:', filter);
    try {
      const response = await bottlesApi.getBottles(filter);
      console.log('🍷 Redux - fetchBottles réponse:', response);
      return response;
    } catch (error) {
      console.error('🍷 Redux - fetchBottles erreur:', error);
      throw error;
    }
  }
);

export const fetchBottle = createAsyncThunk(
  'bottles/fetchBottle',
  async (reference: string) => {
    // On utilise findBottleById car il prend la reference comme string
    // et utilise l'endpoint POST /bouteille/find qui semble plus fiable
    console.log('🍷 Redux - fetchBottle appelé avec reference:', reference);
    try {
      // D'abord essayer de trouver la bouteille dans la liste actuelle
      // Sinon, utiliser l'API pour la charger
      const response = await bottlesApi.getBottle(reference);
      return response;
    } catch (error) {
      console.error('🍷 Redux - fetchBottle erreur:', error);
      throw error;
    }
  }
);

export const createBottle = createAsyncThunk(
  'bottles/createBottle',
  async (bottle: CreateBottleDto) => {
    const response = await bottlesApi.createBottle(bottle);
    return response;
  }
);

export const createBottleWithImage = createAsyncThunk(
  'bottles/createBottleWithImage',
  async ({ bottle, image, reference }: { bottle: CreateBottleDto; image?: File; reference: string }) => {
    const response = await bottlesApi.createBottleWithImage(bottle, image, reference);
    return response;
  }
);

export const updateBottle = createAsyncThunk(
  'bottles/updateBottle',
  async ({ id, bottle }: { id: string; bottle: UpdateBottleDto }) => {
    const response = await bottlesApi.updateBottle(id, bottle);
    return response;
  }
);

export const deleteBottle = createAsyncThunk(
  'bottles/deleteBottle',
  async (id: string) => {
    await bottlesApi.deleteBottle(id);
    return id;
  }
);

export const archiveBottle = createAsyncThunk(
  'bottles/archiveBottle',
  async ({ reference, archiveData }: { reference: string; archiveData: ArchiveBottleDto }) => {
    const response = await bottlesApi.archiveBottle(reference, archiveData);
    return response;
  }
);

export const fetchArchivedBottles = createAsyncThunk(
  'bottles/fetchArchivedBottles',
  async (filter?: BottlesFilter) => {
    const response = await bottlesApi.getArchivedBottles(filter);
    return response;
  }
);

const bottlesSlice = createSlice({
  name: 'bottles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentBottle: (state) => {
      state.currentBottle = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBottles.pending, (state) => {
        console.log('🍷 Redux - fetchBottles.pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBottles.fulfilled, (state, action: PayloadAction<PaginatedResponse<Bottle>>) => {
        console.log('🍷 Redux - fetchBottles.fulfilled avec données:', action.payload);
        state.isLoading = false;
        state.bottles = action.payload.data;
        state.totalBottles = action.payload.total;
        state.currentPage = action.payload.page;
        state.totalPages = action.payload.totalPages;
        console.log('🍷 Redux - State mis à jour:', { 
          bottles: state.bottles.length, 
          totalBottles: state.totalBottles 
        });
      })
      .addCase(fetchBottles.rejected, (state, action) => {
        console.error('🍷 Redux - fetchBottles.rejected:', action.error);
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement des bouteilles';
      })
      .addCase(fetchBottle.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBottle.fulfilled, (state, action: PayloadAction<Bottle>) => {
        state.isLoading = false;
        state.currentBottle = action.payload;
      })
      .addCase(createBottle.fulfilled, (state, action: PayloadAction<Bottle>) => {
        state.bottles.unshift(action.payload);
        state.totalBottles += 1;
      })
      .addCase(createBottleWithImage.fulfilled, (state, action: PayloadAction<Bottle>) => {
        state.bottles.unshift(action.payload);
        state.totalBottles += 1;
      })
      .addCase(updateBottle.fulfilled, (state, action: PayloadAction<Bottle>) => {
        const index = state.bottles.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.bottles[index] = action.payload;
        }
        if (state.currentBottle?.id === action.payload.id) {
          state.currentBottle = action.payload;
        }
      })
      .addCase(deleteBottle.fulfilled, (state, action: PayloadAction<string>) => {
        state.bottles = state.bottles.filter((b) => b.id !== action.payload);
        state.totalBottles -= 1;
      })
      .addCase(archiveBottle.fulfilled, (state, action: PayloadAction<Bottle>) => {
        state.bottles = state.bottles.filter((b) => b.id !== action.payload.id);
        state.totalBottles -= 1;
      })
      .addCase(fetchArchivedBottles.pending, (state) => {
        console.log('🍷 Redux - fetchArchivedBottles.pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArchivedBottles.fulfilled, (state, action: PayloadAction<PaginatedResponse<Bottle>>) => {
        console.log('🍷 Redux - fetchArchivedBottles.fulfilled avec données:', action.payload);
        state.isLoading = false;
        state.archivedBottles = action.payload.data;
        state.totalArchivedBottles = action.payload.total;
        state.error = null;
      })
      .addCase(fetchArchivedBottles.rejected, (state, action) => {
        console.error('🍷 Redux - fetchArchivedBottles.rejected:', action.error);
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement des bouteilles archivées';
      });
  },
});

export const { clearError, clearCurrentBottle } = bottlesSlice.actions;
export default bottlesSlice.reducer;